import PropTypes from 'prop-types'
import { forwardRef, useImperativeHandle, useLayoutEffect, useRef } from 'react'

import { C, E, clamp, lerp, mixColor, prog, spring } from '../../clip-geometry'
import { BORDER, BX, BY, SH, SW, blink, lookDir } from '../stage'

// Variante "Shapes": Dreieck, Quadrat und Kreis aus dem Brand-Shape-Vokabular.
// Sie fallen wie Tetris-Steine in den Button und werden zum Gesicht, Gamification-Nod.

const FACE = [
  { k: 'circle', c: C.pink, x: -8, y: -4, rot: 0, h: 5.2 },
  { k: 'square', c: C.green, x: 8, y: -4, rot: 0, h: 4.8 },
  { k: 'tri', c: C.yellow, x: 0, y: 8.5, rot: 180, h: 5.2 }
]
const INTRO_END = 2.6
const FALL = 0.55
const fallStart = (i) => 0.3 + i * 0.3
const TRI = 'M0 -6.9 L6 3.5 L-6 3.5 Z'

function formation(i, t, ui) {
  const s = FACE[i]
  const d = lookDir(ui)
  const eye = i < 2
  const lk =
    ui.open > 0.5 ? [0, -2] : [d[0] * 1.8 * ui.near, d[1] * 1.8 * ui.near]
  let x = s.x + (eye ? lk[0] : 0)
  let y = s.y + (eye ? lk[1] : 0) + Math.sin(t * 2) * 0.8
  let rot = s.rot
  let sx = 1
  let sy = 1

  if (t > INTRO_END) {
    // Quadrat dreht sich ab und zu einmal um 90 Grad, der Mund wackelt
    const ph = t % 5.2
    if (i === 1 && ph < 0.6) rot += 90 * E.outBack(prog(ph, 0, 0.5))
    const ph2 = (t + 2.6) % 5.2
    if (i === 2 && ph2 < 0.8)
      rot += 14 * Math.sin(ph2 * 20) * Math.exp(-ph2 * 5)
    if (eye) sy *= blink(t, 3.8)
  }

  // Level-Up-Sprung im Intro
  const j = prog(t, 1.55 + i * 0.06, 1.95 + i * 0.06)
  y -= 9 * Math.sin(Math.PI * j)

  // Hover: Welle, Mund wird breiter
  y -= 4 * Math.max(0, Math.sin(t * 10 - i * 0.9)) * ui.hover
  if (i === 2) {
    sx *= 1 + 0.3 * ui.hover + 0.2 * ui.open
    sy *= 1 + 0.1 * ui.open
  }

  // Nachdenken: Karussell um die Mitte
  if (ui.thinking > 0.001) {
    const a = t * 5 + (i * 2 * Math.PI) / 3
    const w = E.inOutCubic(ui.thinking)
    x = lerp(x, Math.cos(a) * 10, w)
    y = lerp(y, Math.sin(a) * 10, w)
    rot = lerp(rot, rot + t * 430, w)
    sx = lerp(sx, 0.85, w)
    sy = lerp(sy, 0.85, w)
  }
  return { x, y, rot, sx, sy, pupil: eye ? [lk[0] * 0.9, lk[1] * 0.9] : null }
}

function frame(t, ui, el) {
  const hoverS = 1 + 0.05 * ui.hover
  el.circle.setAttribute('r', Math.max(0, 30 * spring(t, 8, 13) * hoverS))
  el.circle.setAttribute('stroke', mixColor(BORDER, C.blue, ui.hover))

  el.shapes.forEach((g, i) => {
    const f = formation(i, t, ui)
    const s0 = fallStart(i)
    if (t < s0) {
      g.style.opacity = 0
      return
    }
    g.style.opacity = 1
    const x = BX + f.x
    let y = BY + f.y
    let { rot, sx, sy } = f
    const p = prog(t, s0, s0 + FALL)
    if (p < 1) {
      // Tetris: fallen mit 90-Grad-Schritten
      y = lerp(-40, y, p * p)
      rot = FACE[i].rot + (3 - Math.min(3, Math.floor(p * 4))) * 90
      sy = 1.1
      sx = 0.92
    } else {
      const tau = t - (s0 + FALL)
      const sq = 1 - 0.32 * Math.exp(-9 * tau) * Math.cos(17 * tau)
      sy *= sq
      sx *= 1 + (1 - sq) * 0.8
      y += FACE[i].h * (1 - sq)
    }
    const pupil = g.querySelector('.as-pupil')
    if (pupil && f.pupil)
      pupil.setAttribute(
        'transform',
        `translate(${f.pupil[0].toFixed(2)} ${f.pupil[1].toFixed(2)})`
      )
    g.setAttribute(
      'transform',
      `translate(${x.toFixed(2)} ${y.toFixed(2)}) rotate(${rot.toFixed(1)}) scale(${(sx * hoverS).toFixed(3)} ${(sy * hoverS).toFixed(3)})`
    )
  })

  // "LEVEL UP!" steigt auf
  const q = prog(t, 1.5, 2.3)
  const show = q > 0 && q < 1
  el.level.style.opacity = show ? (q < 0.75 ? 1 : 1 - (q - 0.75) / 0.25) : 0
  if (show) {
    const pop = E.outBack(prog(t, 1.5, 1.7))
    el.level.setAttribute(
      'transform',
      `translate(${BX} ${BY - 42 - 22 * E.outCubic(q)}) scale(${clamp(pop, 0, 1.2)})`
    )
  }
}

const ShapeMark = ({ k, c }) => {
  if (k === 'circle')
    return (
      <>
        <circle r='5.2' fill={c} />
        <circle className='as-pupil' r='2.1' fill={C.dark} />
      </>
    )
  if (k === 'square')
    return (
      <>
        <rect x='-4.8' y='-4.8' width='9.6' height='9.6' rx='1.6' fill={c} />
        <circle className='as-pupil' r='2.1' fill={C.dark} />
      </>
    )
  return (
    <path
      d={TRI}
      fill={c}
      stroke={c}
      strokeWidth='1.6'
      strokeLinejoin='round'
    />
  )
}

ShapeMark.propTypes = { k: PropTypes.string, c: PropTypes.string }

const ShapesStage = forwardRef(function ShapesStage(_, ref) {
  const svgRef = useRef(null)
  const elRef = useRef(null)

  useLayoutEffect(() => {
    const svg = svgRef.current
    elRef.current = {
      circle: svg.querySelector('.as-circle'),
      shapes: Array.from(svg.querySelectorAll('.as-shape')),
      level: svg.querySelector('.as-level')
    }
  }, [])

  useImperativeHandle(ref, () => ({
    frame: (t, ui) => elRef.current && frame(t, ui, elRef.current)
  }))

  return (
    <svg
      ref={svgRef}
      width={SW}
      height={SH}
      viewBox={`0 0 ${SW} ${SH}`}
      className='absolute inset-0 overflow-visible'
      aria-hidden='true'
    >
      <circle
        className='as-circle'
        cx={BX}
        cy={BY}
        r='0'
        fill='#fff'
        strokeWidth='1.5'
      />
      {FACE.map((s) => (
        <g key={s.k} className='as-shape' opacity='0'>
          <ShapeMark k={s.k} c={s.c} />
        </g>
      ))}
      <text
        className='as-level'
        textAnchor='middle'
        fontFamily='"Fira Sans", system-ui, sans-serif'
        fontWeight='700'
        fontSize='12'
        fill={C.blue}
        opacity='0'
      >
        Level up!
      </text>
    </svg>
  )
})

export function ShapesAvatar({ size = 36 }) {
  return (
    <svg width={size} height={size} viewBox='0 0 100 100' aria-hidden='true'>
      <circle
        cx='50'
        cy='50'
        r='48.5'
        fill='#fff'
        stroke={BORDER}
        strokeWidth='3'
      />
      {FACE.map((s) => (
        <g
          key={s.k}
          transform={`translate(${50 + s.x * 2.6} ${50 + s.y * 2.6}) rotate(${s.rot}) scale(2.6)`}
        >
          <ShapeMark k={s.k} c={s.c} />
        </g>
      ))}
    </svg>
  )
}

ShapesAvatar.propTypes = { size: PropTypes.number }

export default {
  id: 'shapes',
  name: 'Shapes',
  description:
    'Kreis, Quadrat und Dreieck aus der Brand fallen wie Tetris-Steine in den Button und werden zum Gesicht. Ein Nod an die gamifizierten Assessments, inklusive Level up. Beim Nachdenken drehen sie sich als Karussell.',
  introEnd: INTRO_END,
  Stage: ShapesStage,
  Avatar: ShapesAvatar
}
